import React, { useEffect, useMemo, useState } from 'react';
import PageWrapper from '../../components/PageWrapper';
import { Button, Grid2, useTheme, Box, Typography, Drawer, Chip, DialogActions, DialogContent, DialogTitle, Dialog, TextField, Alert, Snackbar, alpha, Tooltip, Divider, LinearProgress } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePlanDeleteMutation, usePlanGetInfoMutation, usePlanPublishMutation, usePlanResourceAddMutation, usePlanUnpublishMutation, usePlanUpdateMutation, usePlanVideoDeleteMutation, usePlanVideoUploadMutation } from '../../services/plans';
import { useAuthEffects } from '../../components/Auth';
import { PlanDocumentItem, PlanListItemSource, ResourceListItem } from '../../types';
import { SimpleFormField, SimpleFormPropertyType, SimpleFormTemplateProperty, useSimpleForm } from '../../components/SimpleForm';
import { PlanDetails, PlanTagWithName, Tool } from './types';
import { SimpleList, SimpleListItem } from '../../components/SimpleList';
import { usePlanResourceDeleteMutation, usePlanResourceUpdateMutation } from '../../services/planResources';
import FileDropzone from '../../components/FileDropZone';
import { PlanAddMaterialDialog, PlanAddToolDialog } from './components/PlanAddResourceDialog';
import { PlanMaterialEditDialog, PlanToolEditDialog } from './components/PlanResourceEditDialog';
import { useGetToolOptionsQuery } from '../../services/tools';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { translateToolName } from '../../i18n/utils';
import { usePlanTagsCreateMutation, usePlanTagsDeleteMutation } from '../../services/planTags';
import { useFindGlobalTagsQuery } from '../../services/tags';
import { useAppSelector } from '../../hooks';
import { selectPlanDetails } from './planSlice';
import { SimpleVideoList } from '../../components/SimpleVideoList';
import { SimpleImageList } from '../../components/SimpleImageList';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { usePlanDocumentDeleteMutation, usePlanDocumentsFindByPlanIdQuery, usePlanDocumentUploadMutation } from '../../services/planDocuments';
import { usePlanImageDeleteMutation, usePlanImagesReorderMutation, usePlanImageUploadMutation, usePlanImagesFindByPlanIdQuery } from '../../services/planImages';
import DownloadIcon from '@mui/icons-material/Download';
import { PlanDocumentEditDialog } from './components/PlanDocumentEditDialog';

const DISABLE_DOCUMENTS = true;

export default function PlanEditView() {
	useAuthEffects();

	const { id } = useParams();
	const theme = useTheme();
	const plan = useAppSelector(selectPlanDetails);
	const [isPublished, setIsPublished] = useState<boolean>(false);
	const [planGetInfoMutation] = usePlanGetInfoMutation();
	const [planUpdateMutation] = usePlanUpdateMutation();
	const [planPublishMutation] = usePlanPublishMutation();
	const [planUnpublishMutation] = usePlanUnpublishMutation();
	const [planImageUploadMutation, {isLoading: isUploadingImage}] = usePlanImageUploadMutation();
	const [planImageDeleteMutation] = usePlanImageDeleteMutation();
	const [planImagesReorderMutation] = usePlanImagesReorderMutation();
	const [planDocumentUploadMutation, {isLoading: isUploadingDocument}] = usePlanDocumentUploadMutation();
	const [planDocumentDeleteMutation] = usePlanDocumentDeleteMutation();
	const [planDeleteMutation] = usePlanDeleteMutation();
	const [planResourceAddMutation] = usePlanResourceAddMutation();
	const [planResourceUpdateMutation] = usePlanResourceUpdateMutation();
	const [planResourceDeleteMutation] = usePlanResourceDeleteMutation();
	const [planVideoUploadMutation, {isLoading: isUploadingVideo}] = usePlanVideoUploadMutation();
	const [planVideoDeleteMutation] = usePlanVideoDeleteMutation();
	const [planTagsCreateMutation] = usePlanTagsCreateMutation();
	const [planTagsDeleteMutation] = usePlanTagsDeleteMutation();
	const [isAddTagDialogOpen, setIsAddTagDialogOpen] = useState(false);
	const [isAddMaterialDialogOpen, setIsAddMaterialDialogOpen] = useState(false);
	const [isAddToolDialogOpen, setIsAddToolDialogOpen] = useState(false);
	const [editMaterialDialogItem, setEditMaterialDialogItem] = useState<ResourceListItem | null>(null);
	const [editToolDialogItem, setEditToolDialogItem] = useState<ResourceListItem | null>(null);
	const [isToolshedDrawerOpen, setIsToolshedDrawerOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [deletePassword, setDeletePassword] = useState('');
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [editPlanDocumentId, setEditPlanDocumentId] = useState<number | null>(null);
	const {data: globalTags = []} = useFindGlobalTagsQuery({}, {skip: !plan});
	const {data: planDocuments = []} = usePlanDocumentsFindByPlanIdQuery(Number(id), {skip: !id});
	const {data: planImages = []} = usePlanImagesFindByPlanIdQuery(Number(id), {skip: !id});
	const editPlanDocumentListItem = useMemo(() => planDocuments.find((document) => document.id === editPlanDocumentId) ?? null, [planDocuments, editPlanDocumentId]);
	
	const navigate = useNavigate();
	const resources = useMemo(() => plan ? plan.resources : [], [plan]);
	
	const PLAN_EDIT_FORM_TEMPLATE: SimpleFormTemplateProperty<PlanDetails>[] = [{
		name: 'title',
		label: 'Title',
		type: SimpleFormPropertyType.String,
		disabled: Boolean(plan?.source),
	}, {
		name: 'alternativeTitle',
		label: 'Alternative title',
		type: SimpleFormPropertyType.String,
	}, {
		name: 'description',
		label: 'Description',
		type: SimpleFormPropertyType.Text,
		multiline: true,
	}, {
		name: 'materialsCost',
		label: 'Estimated materials cost',
		type: SimpleFormPropertyType.String,
	}, {
		name: 'timeCost',
		label: 'Estimated time cost',
		type: SimpleFormPropertyType.String,
	}, {
		name: 'instructions',
		label: 'Instructions',
		type: SimpleFormPropertyType.Text,
		multiline: true,
	}, {
		name: 'cutsUrl',
		label: 'Cuts URL',
		type: SimpleFormPropertyType.String,
	}, {
		name: 'password',
		label: 'Passkey',
		type: SimpleFormPropertyType.String,
	}, ...(plan?.source === PlanListItemSource.Pinterest ? [{
		name: 'pinId',
		label: 'Pinterest pin id',
		type: SimpleFormPropertyType.String,
		// disabled: true,
	}] : []) as SimpleFormTemplateProperty<PlanDetails>[],
	...(plan?.source === PlanListItemSource.Instagram ? [{
		name: 'igId',
		label: 'Instagram post id',
		type: SimpleFormPropertyType.String,
		// disabled: true,
	}] : []) as SimpleFormTemplateProperty<PlanDetails>[],
	...(plan?.source === PlanListItemSource.Youtube ? [{
		name: 'ytId',
		label: 'Youtube video id',
		type: SimpleFormPropertyType.String,
		// disabled: true,
	}] : []) as SimpleFormTemplateProperty<PlanDetails>[]];
	const {getFormData, resetFormData, handleFormFieldChange, hasFormChanged} = useSimpleForm<PlanDetails>(PLAN_EDIT_FORM_TEMPLATE);
	
	useEffect(() => {
		if (isDeleteDialogOpen) {
			setDeletePassword('');
		}
	}, [isDeleteDialogOpen]);

	useEffect(() => {
		if (id) {
			planGetInfoMutation(id);
		}
	}, [id]);

	useEffect(() => {
		if (plan) {
			resetFormData(plan);
			setIsPublished(Boolean(plan.publishedAt));
		}
	}, [plan]);

	/** handlers */
	const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = getFormData(e);
		planUpdateMutation({...formData, id}).unwrap().then(() => {
			setSuccessMessage('Changes saved.');
		}).catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to update plan');
		});
	};

	const handleIsPublishedChange = () => {
		if (!plan) {
			return;
		}
		if (isPublished) {
			planUnpublishMutation(plan.id).unwrap().catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to update plan');
			});
		} else {
			planPublishMutation(plan.id).unwrap().catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to update plan');
			});
		}
		setIsPublished(!isPublished);
	};

	const handleDeleteImageClick = (imageId: number) => {
		planImageDeleteMutation(imageId).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to delete image');
		});
	};

	const handleDeletePlan = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (plan) {
			planDeleteMutation(plan.id).unwrap().then(() => {
				navigate('/plans');
			}).catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to delete plan');
			});
		}
	};

	const handleAddMaterialClick = () => {
		setIsAddMaterialDialogOpen(true);
	};
	
	const handleAddToolClick = () => {
		setIsAddToolDialogOpen(true);
		setIsToolshedDrawerOpen(false);
	};

	const handleToolAddToPlanClick = (tool: Tool) => {
		if (plan) {
			planResourceAddMutation({ 
				isTool: true,
				uom: 'pcs',
				planId: plan.id,
				toolId: tool.id,
				name: tool.key, // TODO: not really necessary
				quantity: '1',
			});
		}
	};

	const handleAddToolSearchButtonClick = () => {
		setIsAddToolDialogOpen(false);
		setIsToolshedDrawerOpen(true);
	};

	const handleToolRemoveFromPlanClick = (tool: Tool) => {
		if (plan) {
			const resource = planToolsMap.get(tool.id);
			if (resource) {
				planResourceDeleteMutation(resource.id).unwrap().catch((error) => {
					setErrorMessage((error as any)?.data?.message ?? 'Failed to delete tool');
				});
			}
		}
	};

	const handleAddMaterialSubmit = (formData: Partial<ResourceListItem>) => {
		if (plan) {
			planResourceAddMutation({ 
				...formData,
				planId: plan.id,
			}).unwrap().catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to add material');
			});
		}
	};

	const handleAddToolSubmit = (formData: Partial<ResourceListItem>) => {
		if (plan) {
			planResourceAddMutation({ 
				...formData,
				isTool: true,
				uom: 'pcs',
				planId: plan.id,
			}).unwrap().catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to add tool');
			});
		}
	};

	const handleEditMaterialSubmit = (formData: Partial<ResourceListItem>) => {
		if (plan) {
			planResourceUpdateMutation({
				...formData,
				id: formData.id,
			}).unwrap().catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to update material');
			});
		}
		setEditMaterialDialogItem(null);
	};

	const handleEditToolSubmit = (formData: Partial<ResourceListItem>) => {
		if (plan) {
			planResourceUpdateMutation({
				...formData,
				id: formData.id,
			}).unwrap().catch((error) => {
				setErrorMessage((error as any)?.data?.message ?? 'Failed to update tool');
			});
		}
		setEditToolDialogItem(null);
	};

	const handleResourceDelete = (resourceId: number) => {
		planResourceDeleteMutation(resourceId).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to delete resource');
		});
	};

	const handleFileUpload = (file: File) => {
		const formData = new FormData();
		formData.append('file', file);
		planImageUploadMutation({id: Number(id), formData}).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to upload image');
		});
	};

	const handleVideoUpload = (file: File) => {
		const formData = new FormData();
		formData.append('file', file);
		planVideoUploadMutation({id: Number(id), formData}).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to upload video');
		});
	};

	const handleDeleteVideoClick = (videoId: number) => {
		planVideoDeleteMutation(videoId).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to delete video');
		});
	};

	const {data: toolOptions} = useGetToolOptionsQuery();
	const {t} = useTranslation();
	const planToolsMap = useMemo(() => (plan?.resources ?? []).reduce((acc, resource) => {
		if (resource.isTool) {
			acc.set(resource.toolId, resource);
		}
		return acc;
	}, new Map<number, ResourceListItem>()), [plan]);
	const toolsByIdsMap = useMemo(() => (toolOptions ?? []).reduce((acc, tool) => {
		acc.set(tool.id, tool);
		return acc;
	}, new Map<number, Tool>()), [toolOptions]);
	
	const planTagsMap = useMemo(() => (plan?.tags ?? []).reduce((acc, tag) => {
		acc.set(tag.tagName, tag);
		return acc;
	}, new Map<string, PlanTagWithName>()), [plan]);

	const populatedTags = useMemo(() => {
		const uniqueTags = new Map<string, {tagName: string, id: number, isApproved: boolean}>();
		globalTags?.forEach((tag) => {
			uniqueTags.set(tag.name, {tagName: tag.name, id: tag.id, isApproved: tag.isApproved});
		});
		plan?.tags.forEach((tag) => {
			uniqueTags.set(tag.tagName, {tagName: tag.tagName, id: tag.tagId, isApproved: tag.isApproved});
		});
		return Array.from(uniqueTags).map(([, tag]) => {
			const planTag = planTagsMap.get(tag.tagName);
			return {tagName: tag.tagName, id: planTag?.tagId, isApproved: tag.isApproved};
		});
	}, [globalTags, plan]);

	const handleTagClick = (tagName: string) => {
		if (!plan) {
			return;
		}
		const planTag = plan.tags.find((t) => t.tagName === tagName);
		if (planTag) {
			planTagsDeleteMutation({ id: planTag.id });
		} else {
			const tag = globalTags?.find((t) => t.name === tagName);
			if (tag) {
				planTagsCreateMutation({planId: plan.id, name: tag.name});
			}
		}
	};

	const handleIsVendorsEnabledChange = () => {
		if (!plan) {
			return;
		}
		planUpdateMutation({id: plan.id, isVendorsEnabled: !plan.isVendorsEnabled});
	};

	const handleIsVerifiedByAuthorChange = () => {
		if (!plan) {
			return;
		}
		planUpdateMutation({id: plan.id, isVerifiedByAuthor: !plan.isVerifiedByAuthor});
	};

	const handleReorderImages = (reorderedItems: {id: number, rank: number}[]) => {
		planImagesReorderMutation(reorderedItems).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to reorder images');
		});
	};

	const handleDocumentUpload = (file: File) => {
		if (!plan) {
			return;
		}
		const formData = new FormData();
		formData.append('file', file);
		formData.append('planId', plan.id.toString());
		planDocumentUploadMutation(formData);
	};

	const handleDeletePlanDocument = (documentId: number) => {
		planDocumentDeleteMutation(documentId).unwrap().catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to delete document');
		});
	};

	const handleAddTagClick = () => {
		setIsAddTagDialogOpen(true);
	};

	const handleAddTagSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!plan) {
			return;
		}
		const formData = new FormData(e.target as HTMLFormElement);
		planTagsCreateMutation({name: formData.get('name') as string, planId: plan.id}).unwrap().then(() => {
			setSuccessMessage('Tag added.');
		}).catch((error) => {
			setErrorMessage((error as any)?.data?.message ?? 'Failed to add tag');
		});
		setIsAddTagDialogOpen(false);
	};

	return <PageWrapper maxWidth='md'>
		<Box display='flex' flexDirection='column' gap={2} mt={2}>
			{plan?.password && <Box display='flex' justifyContent='center'>
				<Tooltip title={t('planView.passkeyInfo')}>
					<Alert sx={{borderRadius: 10, bgcolor: alpha(theme.palette.text.primary, 0.05), color: 'primary.main', '& .MuiAlert-icon': {color: 'primary.main'}}} icon={<LockIcon />}>
						<Typography variant='body1'>{t('planView.passKeyLabel')}</Typography>
					</Alert>
				</Tooltip>
			</Box>}

			<div>
				<Button startIcon={<ChevronLeftIcon />} component={Link} to='/plans'>Back to projects</Button>
			</div>
			{plan && <form onSubmit={handleFormSubmit}>
				<Grid2 container spacing={2} rowSpacing={4}>
					<Grid2 size={{xs: 12, md: 6}} container spacing={1} alignContent='baseline'>
						{PLAN_EDIT_FORM_TEMPLATE.map((property) => <Box key={`${plan.id}-${property.name}`} sx={{width: '100%'}}>
							<SimpleFormField property={property} defaultValue={plan[property.name]} onChange={handleFormFieldChange} />
						</Box>)}
						<Grid2 size={12}>
							<Button disabled={!hasFormChanged} variant='text' color='inherit' onClick={() => window.location.reload()}>Reset</Button>
							&nbsp;
							<Button type='submit' disabled={!hasFormChanged} variant='contained'>Save</Button>
						</Grid2>
					</Grid2>
					<Grid2 size={{xs: 12, md: 6}}>
						<Typography variant='h6' mb={1}>Images</Typography>
						<SimpleImageList images={planImages} onDeleteImageClick={handleDeleteImageClick} onReorderSubmit={handleReorderImages} sx={{mb: 1}} />
						{isUploadingImage && <LinearProgress />}
						<FileDropzone onFileUpload={handleFileUpload} id='file-upload-image' accept='image/*' />
					</Grid2>
					<Grid2 size={{xs: 12}}>
						<Typography variant='h6' mb={1}>Instructional videos</Typography>
						<SimpleVideoList
							videos={plan.videos
								.filter((video) => video.source === 'manual' && video.videoUrl)
								.map((video) => ({
									videoUrl: video.videoUrl || '',
									id: video.id,
									source: 'manual',
								}))}
							handleDeleteVideoClick={handleDeleteVideoClick}
						/>
						{isUploadingVideo && <LinearProgress />}
						<FileDropzone onFileUpload={handleVideoUpload} id='file-upload-video' accept='video/*' />
					</Grid2>
					{!DISABLE_DOCUMENTS && <Grid2 size={{xs: 12}}>
						<Typography variant='h6' mb={1}>Documents</Typography>
						{planDocuments.length > 0 && <Box mb={1}>
							{planDocuments.map((document: PlanDocumentItem) => (
								<SimpleListItem key={document.id} label={document.name} actions={[{
									icon: <DownloadIcon />,
									component: Link,
									to: document.fileUrl,
								}, {
									icon: <DeleteIcon />, 
									onClick: () => handleDeletePlanDocument(document.id)}]
								} />
							))}
						</Box>}
						{isUploadingDocument && <LinearProgress />}
						<FileDropzone onFileUpload={handleDocumentUpload} id='file-upload-document' accept='application/pdf' />
					</Grid2>}
					<Grid2 size={{xs: 12}}>
						<Typography variant='h6' mb={1}>Preview</Typography>
						<Button component={Link} to={`/plans/${plan.id}`} startIcon={<VisibilityIcon />} variant='contained' color='info'>View project page</Button>
					</Grid2>
					<Grid2 size={{xs: 12}}>
						<Typography variant='h6' mb={1}>Tags</Typography>
						<Box display='flex' flexWrap='wrap' gap={1}>
							{populatedTags?.map((tag) => <Chip key={tag.tagName} clickable onClick={() => handleTagClick(tag.tagName)} label={t(`tags.${tag.tagName}`, {defaultValue: `${tag.tagName[0].toUpperCase()}${tag.tagName.slice(1)}`})} color={tag.id ? 'primary' : 'default'} sx={{filter: tag.isApproved ? 'none' : 'opacity(0.5)'}} />)}
						</Box>
						<Button startIcon={<AddIcon />} variant='text' color='primary' onClick={() => handleAddTagClick()} sx={{mt: 1}}>{t('planEditView.addTag')}</Button>
					</Grid2>
				</Grid2>
			</form>}
			<Box mt={3}>
				<Typography variant='h6'>Recipe</Typography>
				<SimpleList 
					actions={[
						{
							label: t('planEditView.addMaterial'),
							icon: <AddIcon />,
							onClick: () => handleAddMaterialClick(),
						}]} 
					items={resources.filter((resource) => !resource.isTool).map((resource) => ({
						label: resource.name,
						secondary: resource.description,
						properties: [...(resource.isOptional ? [{label: 'Optional'}] : []), {
							label: resource.quantity,
							secondary: resource.uom,
						}],
						onDeleteClick: () => handleResourceDelete(resource.id),
						onEditClick: () => setEditMaterialDialogItem(resource),
					}))} />
				<SimpleList 
					actions={[
						{
							label: t('planEditView.addTool'),
							icon: <AddIcon />,
							onClick: () => handleAddToolClick(),
						}]} 
					items={resources.filter((resource) => resource.isTool).map((resource) => {
						const tool = resource.toolId ? toolsByIdsMap.get(resource.toolId) : undefined;
						return {
							label: tool ? translateToolName(tool.key) : resource.name,
							secondary: resource.description,
							imgUrl: tool?.imgUrl,
							properties: [
								...(resource.isOptional ? [{label: 'Optional'}] : []),
								...(resource.isHeavyTool ? [{label: 'Heavy tool', isWarning: true}] : []),
								...(Number(resource.quantity) > 1 ? [{
									label: resource.quantity,
									secondary: resource.uom,
								}] : []),
							],
							onDeleteClick: () => handleResourceDelete(resource.id),
							onEditClick: () => setEditToolDialogItem(resource),
						};
					})}
				/>
			</Box>
			<Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mt: 2}}>
				<Typography variant='h6'>Settings</Typography>
				<div>
					<SimpleFormField property={{name: 'isPublished', label: 'Is published', type: SimpleFormPropertyType.Toggle}} value={isPublished} onChange={handleIsPublishedChange} />
				</div>
				<SimpleFormField property={{name: 'isVendorsEnabled', label: 'Allow other vendors to sell build kits', type: SimpleFormPropertyType.Toggle}} value={plan?.isVendorsEnabled} onChange={handleIsVendorsEnabledChange} />
				<SimpleFormField property={{name: 'isVerifiedByAuthor', label: 'I am the author', type: SimpleFormPropertyType.Toggle}} value={plan?.isVerifiedByAuthor} onChange={handleIsVerifiedByAuthorChange} />
			</Box>
			<Divider />
			<Box>
				<Button startIcon={<DeleteIcon />} variant='text' color='error' onClick={() => setIsDeleteDialogOpen(true)}>Delete plan</Button>
			</Box>
		</Box>
		<PlanAddMaterialDialog
			open={isAddMaterialDialogOpen}
			onClose={() => setIsAddMaterialDialogOpen(false)} 
			onSubmit={handleAddMaterialSubmit}
			defaultValues={{
				uom: 'pcs',
				quantity: '1',
			}}
		/>
		<PlanAddToolDialog
			open={isAddToolDialogOpen}
			onClose={() => setIsAddToolDialogOpen(false)} 
			onSubmit={handleAddToolSubmit}
			defaultValues={{
				quantity: '1',
			}}
			onSearchButtonClick={handleAddToolSearchButtonClick}
		/>
		<PlanMaterialEditDialog
			item={editMaterialDialogItem}
			open={!!editMaterialDialogItem}
			onClose={() => setEditMaterialDialogItem(null)}
			onSubmit={handleEditMaterialSubmit}
		/>
		<PlanToolEditDialog
			item={editToolDialogItem}
			open={!!editToolDialogItem}
			onClose={() => setEditToolDialogItem(null)}
			onSubmit={handleEditToolSubmit}
		/>
		<Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} fullWidth maxWidth='xs'>
			<DialogTitle sx={{backgroundColor: 'error.main', color: 'white'}}>Delete this project?</DialogTitle>
			<form onSubmit={handleDeletePlan}>
				<DialogContent sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
					<Typography variant='body1'>This action will permanently delete <b>{plan?.title}</b>.</Typography>
					<TextField name='delete' placeholder='Type "delete" to confirm' required onChange={(e) => setDeletePassword(e.target.value)} autoComplete='off' />
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
					<Button variant='contained' color='error' type='submit' disabled={deletePassword !== 'delete'}>Confirm</Button>
				</DialogActions>
			</form>
		</Dialog>
		<PlanDocumentEditDialog 
			planDocument={editPlanDocumentListItem}
			open={Boolean(editPlanDocumentListItem)}
			onClose={() => setEditPlanDocumentId(null)}
		/>
		<Drawer open={isToolshedDrawerOpen} onClose={() => setIsToolshedDrawerOpen(false)} anchor='right'>
			<Box sx={{maxWidth: 250, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'scroll'}}>
				<SimpleList
					items={toolOptions?.map((tool) => ({
						label: translateToolName(tool.key),
						imgUrl: tool.imgUrl,
						onClick: () => planToolsMap.get(tool.id) ? handleToolRemoveFromPlanClick(tool) : handleToolAddToPlanClick(tool),
						selected: !!planToolsMap.get(tool.id),
					})) ?? []}
				/>
			</Box>
		</Drawer>
		<Dialog open={isAddTagDialogOpen} onClose={() => setIsAddTagDialogOpen(false)} fullWidth maxWidth='xs'>
			<DialogTitle>{t('planEditView.addTag')}</DialogTitle>
			<form onSubmit={handleAddTagSubmit}>
				<DialogContent sx={{pt:0}}>
					<TextField name='name' required label={t('planEditView.tag')} fullWidth />
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsAddTagDialogOpen(false)}>{t('planEditView.cancel')}</Button>
					<Button type='submit' variant='contained' color='primary'>{t('planEditView.addTag')}</Button>
				</DialogActions>
			</form>
		</Dialog>
		<Snackbar open={Boolean(successMessage)} autoHideDuration={6000} onClose={() => setSuccessMessage(null)}>
			<Alert severity='success'>{successMessage}</Alert>
		</Snackbar>
		<Snackbar open={Boolean(errorMessage)} autoHideDuration={6000} onClose={() => setErrorMessage(null)}>
			<Alert severity='error'>{errorMessage}</Alert>
		</Snackbar>
	</PageWrapper>;
}
